import { Form, Formik } from 'formik';
import React, { ReactNode, useMemo } from 'react';
import {
  INavigationItemProps,
  Header3Text,
  NavigationItem,
  FlexRow,
  SpaceWrapper,
  Button,
  SmallBodyText,
  Icon,
  IconType,
  IconSize,
  ModalWindow,
} from 'ui-toolkit';
import { setLocale } from 'yup';
import { cleanParty, IFormParty, ISavePartyAsyncProps, PartySchemaGenerator } from '../../../../../state';
import styled from 'styled-components';
import { ePartyType } from '../../../../../ui-toolkit/enums/ePartyType';
import { DisplayPartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { IParty } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { mergeTests } from '../../../../../ui-toolkit/test-utilities/mergeTests';

interface IEditPartyFormProps {
  party: IParty;
  isNew: boolean;
  onEditEndOnClick: () => void;
  navDeleteStart?: INavigationItemProps;
  onSubmit: (x: ISavePartyAsyncProps) => Promise<void>;
  notification: ReactNode;
  children?: ReactNode;
}

// Schemas are functions so that this locale will apply. yup applies locale to things defined after it's set.
setLocale({
  mixed: {
    required: 'This is required',
  },
  string: {
    email: 'Enter a valid email',
    // eslint-disable-next-line
    min: 'Enter ${min} digits',
    // eslint-disable-next-line
    max: 'Enter ${max} digits',
  },
  number: {},
});

const RedBodyText = styled(SmallBodyText)`
  color: ${(props) => props.theme.colors.errorText};
`;

const StyledForm = styled(Form)`
  grid-column: span 4;
`;

const allParty = {
  id: null,
  first_name: null,
  last_name: null,
  entity_name: null,
  entity_type: null,
};

const useGetInitialFormValues = (party: IParty) => {
  return useMemo(() => {
    if (party.type === ePartyType.person) {
      return {
        ...allParty,
        ...party,
        marital_status: party.marital_status ?? '',
      };
    }
    return { ...allParty, ...party };
  }, [party]);
};

const Title: React.FC<{ isNew: boolean; party: IParty }> = ({ isNew, party }) => {
  return (
    <Header3Text>
      {isNew ? 'Add' : 'Edit'} {DisplayPartyPurpose[party.purpose]}
    </Header3Text>
  );
};

export const EditPartyForm: React.FC<IEditPartyFormProps> = ({
  party,
  onSubmit,
  children,
  isNew,
  onEditEndOnClick,
  navDeleteStart: onDeleteStart,
  notification,
}) => {
  const initialValues = useGetInitialFormValues(party);
  return (
    <Formik
      validateOnBlur={true}
      validateOnChange={true}
      initialValues={initialValues as IFormParty}
      onSubmit={async (values) => {
        const partyToSave = cleanParty(values);
        await onSubmit({ party: partyToSave });
      }}
      validationSchema={PartySchemaGenerator()}
    >
      {({ isSubmitting, dirty }) => (
        <ModalWindow onClose={onEditEndOnClick} title={<Title isNew={isNew} party={party} />} uitype="fit">
          <StyledForm>
            {notification}
            <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
              {children}
            </SpaceWrapper>
            <FlexRow justifyContent={'space-between'}>
              <FlexRow>
                <Button intent="primary" type="submit" disabled={!(!isSubmitting && dirty)}>
                  {isNew ? 'Add' : 'Update'}
                </Button>
                <Button intent="ghost" isInline type="button" onClick={onEditEndOnClick}>
                  Cancel
                </Button>
              </FlexRow>
              {onDeleteStart ? (
                <NavigationItem {...onDeleteStart} data-test-id={mergeTests('party', `delete`)} text="">
                  <FlexRow>
                    <RedBodyText>Delete {DisplayPartyPurpose[party.purpose].toLowerCase()}</RedBodyText>{' '}
                    <Icon type={IconType.Remove} size={IconSize.Small} />
                  </FlexRow>
                </NavigationItem>
              ) : null}
            </FlexRow>
          </StyledForm>
        </ModalWindow>
      )}
    </Formik>
  );
};
